import Vue from 'vue'
import moment from 'moment'

const state = {
    contractProducts: {
        boxes: [],
        products: []
    },
    productDiscounts: {}, // { productId: { discounts: [], applyToAll: false } }
    invoice_freq: 'month',
    start_date: null
}

const mutations = {
    ADD_PRODUCT(state, { type, product }) {
        const exists = state.contractProducts[type].find(existing => existing.id === product.id)
        if (!exists) {
            state.contractProducts[type].push(product)
        }
    },
    REMOVE_PRODUCT(state, { type, id }) {
        const index = state.contractProducts[type].findIndex(item => item.id === id)
        if (index !== -1) {
            state.contractProducts[type].splice(index, 1)
        }
    },
    CLEAR_PRODUCTS(state, type) {
        Vue.set(state.contractProducts, type, [])
    },
    SET_PRODUCT_PROPERTY(state, { type, id, property, value }) {
        const product = state.contractProducts[type].find(item => item.id === id)
        if (product) {
            Vue.set(product, property, value)
        }
    },
    SET_INVOICE_FREQ(state, freq) {
        state.invoice_freq = freq
    },
    SET_START_DATE(state, date) {
        state.start_date = date
    },
    UPDATE_BOX_AMOUNTS(state) {
        const isAfter15 = moment(state.start_date).format('D') >= 15
        state.contractProducts.boxes.forEach(box => {
            let amount = 1 // Default for monthly
            let followUpAmount = 1 // Default for monthly

            if (state.invoice_freq === 'quarter') {
                // For quarterly: pre15 first invoice = 2, follow-up = 3
                // For quarterly: after15 first invoice = 3, follow-up = 3
                amount = isAfter15 ? 3 : 2
                followUpAmount = 3 // Follow-up is always 3 for quarterly
            } else if (state.invoice_freq === 'halfyear') {
                // For halfyearly: pre15 first invoice = 5, follow-up = 6
                // For halfyearly: after15 first invoice = 6, follow-up = 6
                amount = isAfter15 ? 6 : 5
                followUpAmount = 6 // Follow-up is always 6 for halfyearly
            } else if (state.invoice_freq === 'year') {
                // For yearly: pre15 first invoice = 11, follow-up = 12
                // For yearly: after15 first invoice = 12, follow-up = 12
                amount = isAfter15 ? 12 : 11
                followUpAmount = 12 // Follow-up is always 12 for yearly
            }

            Vue.set(box, 'amount', amount)
            Vue.set(box, 'follow_up_amount', followUpAmount)
        })
    },
    SET_PRODUCT_DISCOUNTS(state, { productId, type, discounts, applyToAll }) {
        Vue.set(state.productDiscounts, `${type}_${productId}`, {
            discounts: discounts || [],
            applyToAll: applyToAll !== undefined ? applyToAll : true
        })
    },
    REMOVE_PRODUCT_DISCOUNTS(state, { productId, type }) {
        Vue.delete(state.productDiscounts, `${type}_${productId}`)
    },
    CLEAR_ALL_DISCOUNTS(state) {
        state.productDiscounts = {}
    }
}

const actions = {
    addProduct({ commit, state }, { type, product }) {
        if (type === 'products') {
            product = {
                ...product,
                amount: 1,
                once: product.once !== undefined ? product.once : (product.contract_once === undefined ? true : product.contract_once === 1)
            }
        } else if (type === 'boxes') {
            // For boxes, always set amount to 1 in the contract
            product = {
                ...product,
                amount: 1
            }
        }
        commit('ADD_PRODUCT', { type, product })
    },
    removeProduct({ commit }, { type, id }) {
        commit('REMOVE_PRODUCT', { type, id })
        commit('REMOVE_PRODUCT_DISCOUNTS', { productId: id, type })
    },
    clearProducts({ commit }, type) {
        commit('CLEAR_PRODUCTS', type)
    },
    setProductDiscounts({ commit }, { productId, type, discounts, applyToAll }) {
        commit('SET_PRODUCT_DISCOUNTS', { productId, type, discounts, applyToAll })
    },
    clearAllDiscounts({ commit }) {
        commit('CLEAR_ALL_DISCOUNTS')
    },
    setProductProperty({ commit }, { type, id, property, value }) {
        commit('SET_PRODUCT_PROPERTY', { type, id, property, value })
    },
    setInvoiceFreq({ commit }, freq) {
        commit('SET_INVOICE_FREQ', freq)
    },
    setStartDate({ commit }, date) {
        commit('SET_START_DATE', date)
    },
    async fetchBaseProducts({ commit, rootState }) {
        try {
            const response = await axios.get(`/api/contract/${rootState.user.organisation}/baseproducts`, {
                headers: {'Authorization': 'bearer ' + rootState.authToken}
            })
            if (response && response.data.length) {
                response.data.forEach(product => {
                    const productWithDefaults = {
                        ...product,
                        amount: 1,
                        once: product.contract_once === 1
                    }
                    commit('ADD_PRODUCT', { type: 'products', product: productWithDefaults })
                })
            }
        } catch (error) {
            console.error('[DEBUG] Error fetching base products:', error)
            throw error
        }
    }
}

const getters = {
    getProducts: state => type => state.contractProducts[type],
    isProductSelected: state => (type, id) => {
        return state.contractProducts[type].some(product => product.id === id)
    },
    getProductDiscounts: state => (type, productId) => {
        return state.productDiscounts[`${type}_${productId}`] || { discounts: [], applyToAll: true }
    },
    getAllDiscounts: state => state.productDiscounts
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
